import './globalBenchmarking.scss';
import { useEffect, useMemo, useState } from 'react';
import { Category } from '@blisspointmedia/bpm-types/dist/FilterPane';
import {
  OverallBenchmarkingDataResponse,
  OverallBenchmarkingFilterResponse,
  OverallBenchmarkingKpisResponse,
  OverallBenchmarkingMetricsParams,
  BenchmarkingFilterMap
} from '@blisspointmedia/bpm-types/dist/Benchmarking';
import { App2Channels, getBenchmarkingChannels } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { DateRange } from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { DatePicker } from '@Components/DatePicker';
import { Design } from '@Components/designConstants';
import {
  FetchFilterBar,
  hasFilter,
  useFilterPaneState
} from '@Components/FetchFilterBar/FetchFilterBar';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { Page } from '@Components/Page';
import { useTabbedNav } from '../../utils/hooks/useNav';
import { awaitJSON, BenchmarkingLambdaFetch } from '../../utils/fetch-utils';
import { useSetError } from '../../redux/modals';
import { ChannelGlobalBenchmarking } from './ChannelGlobalBenchmarking';
import {
  DROPDOWN_PRESET_OPTIONS_BY_CHANNEL,
  getMetricsForChannel
} from './globalBenchmarkingConstants';
import { getInitDateRange } from '../XClientBenchmarking/benchmarkingConstants';
import { getFormattedDaysAgo } from '../../utils/date-utils';

const BENCHMARKING_CHANNELS = getBenchmarkingChannels();

const buildDataKey = ({
  channel,
  start,
  end,
  presetKey,
  selectedKpi,
  filterStateJson
}: {
  channel: App2Channels;
  start: string;
  end: string;
  presetKey: string;
  selectedKpi: string | undefined;
  filterStateJson: string;
}): string => `${channel}_${start}_${end}_${presetKey}_${selectedKpi ?? ''}_${filterStateJson}`;

export const GlobalBenchmarking = () => {
  const setError = useSetError();
  const [dateRange, setDateRange] = useState<DateRange>(getInitDateRange);
  const [filterState, setFilterState, resetFilterState] = useFilterPaneState();
  const [dataMap, setDataMap] = useState<Record<string, OverallBenchmarkingDataResponse | 'error'>>(
    {}
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMount, setIsLoadingMount] = useState(true);
  const [filterData, setFilterData] = useState<BenchmarkingFilterMap | undefined>(undefined);
  const [kpisMap, setKpisMap] = useState<OverallBenchmarkingKpisResponse | undefined>(undefined);
  const [selectedKpiByChannel, setSelectedKpiByChannel] = useState<
    Partial<Record<App2Channels, string>>
  >({});
  const [activePresetByChannel, setActivePresetByChannel] = useState<
    Partial<Record<App2Channels, string>>
  >({});

  // In-table search terms, tagged with the data set they were typed against so they reset
  // in the same render when the tab, preset, KPI, date, or filter-pane changes.
  const [tableFilter, setTableFilter] = useState<{ baseKey: string; terms: string[] }>({
    baseKey: '',
    terms: []
  });

  const { start, end } = dateRange;

  const channelNavs = useMemo(
    () => BENCHMARKING_CHANNELS.map(c => ({ key: c.name, label: c.prettyName })),
    []
  );

  const { tab, goToTab } = useTabbedNav<App2Channels>({
    baseURL: 'global-benchmarking',
    defaultKey: channelNavs[0]?.key,
    navs: channelNavs.map(nav => nav.key)
  });

  const activeFilterCategories: Category[] | undefined = useMemo(
    () => (tab && filterData ? filterData[tab] : undefined),
    [tab, filterData]
  );

  const activePresetKey = activePresetByChannel[tab];
  const selectedKpi = selectedKpiByChannel[tab];
  const channelKpis = kpisMap?.[tab];
  const channelHasKpis = Boolean(channelKpis && channelKpis.length > 0);

  const filterStateJson = useMemo(() => {
    const noActiveFilters = !hasFilter(filterState);
    return noActiveFilters ? '' : JSON.stringify(filterState.basic);
  }, [filterState]);

  const baseKey = useMemo(() => {
    if (!tab || !activePresetKey) return '';
    return buildDataKey({
      channel: tab,
      start,
      end,
      presetKey: activePresetKey,
      selectedKpi,
      filterStateJson
    });
  }, [tab, start, end, activePresetKey, selectedKpi, filterStateJson]);

  // Terms typed against another data set are dropped (not just hidden), so returning to that
  // data set later doesn't silently re-apply them under an empty, remounted FilterBar.
  const isStaleTableFilter = tableFilter.baseKey !== baseKey;
  if (isStaleTableFilter && tableFilter.terms.length) {
    setTableFilter({ baseKey, terms: [] });
  }
  const tableFilterTerms = isStaleTableFilter ? [] : tableFilter.terms;
  const tableFilterJson = tableFilterTerms.length ? JSON.stringify(tableFilterTerms) : '';

  const dataKey = baseKey ? `${baseKey}_${tableFilterJson}` : '';

  const activeData = dataKey ? dataMap[dataKey] : undefined;

  // Initial mount: fetch KPIs + filter options in parallel.
  useEffect(() => {
    (async () => {
      try {
        const [kpisRes, filtersRes] = await Promise.all([
          BenchmarkingLambdaFetch('/getOverallBenchmarkingKpis', { params: {} }),
          BenchmarkingLambdaFetch('/getOverallBenchmarkingFilterOptions', { params: {} })
        ]);
        const [kpisJson, filtersJson] = await Promise.all([
          awaitJSON<OverallBenchmarkingKpisResponse>(kpisRes),
          awaitJSON<OverallBenchmarkingFilterResponse>(filtersRes)
        ]);
        setKpisMap(kpisJson);
        setFilterData(filtersJson.filters);
        const initialSelectedKpis: Partial<Record<App2Channels, string>> = {};
        for (const channel of Object.keys(kpisJson) as App2Channels[]) {
          const list = kpisJson[channel];
          if (list && list.length > 0) {
            const [firstKpi] = list;
            initialSelectedKpis[channel] = firstKpi;
          }
        }
        setSelectedKpiByChannel(initialSelectedKpis);
      } catch (e) {
        setError({ message: e.message, reportError: e });
      } finally {
        setIsLoadingMount(false);
      }
    })();
  }, [setError]);

  // Default the active preset for the current channel when none is set yet.
  useEffect(() => {
    if (!tab) return;
    if (activePresetByChannel[tab]) return;
    const presets = DROPDOWN_PRESET_OPTIONS_BY_CHANNEL[tab];
    if (presets && presets.length > 0) {
      setActivePresetByChannel(prev => ({ ...prev, [tab]: presets[0].value }));
    }
  }, [tab, activePresetByChannel]);

  // Fetch metrics when key changes and not cached.
  useEffect(() => {
    if (!tab || !activePresetKey || !dataKey) return;
    if (!kpisMap) return;
    if (dataMap[dataKey]) return;
    if (isLoading) return;
    if (channelHasKpis && !selectedKpi) return;

    const metrics = getMetricsForChannel(tab, channelHasKpis);
    const params: OverallBenchmarkingMetricsParams = {
      channel: tab,
      start,
      end,
      dimensionCols: activePresetKey,
      metricCols: metrics.join(',')
    };
    if (selectedKpi) {
      params.benchmarkingKpi = selectedKpi;
    }
    if (filterStateJson) {
      params.basicFilterState = filterStateJson;
    }
    if (tableFilterJson) {
      params.tableFilter = tableFilterJson;
    }

    (async () => {
      try {
        setIsLoading(true);
        const res = await BenchmarkingLambdaFetch<OverallBenchmarkingMetricsParams>(
          '/getOverallBenchmarkingMetrics',
          { params }
        );

        const data = await awaitJSON<OverallBenchmarkingDataResponse>(res);
        setDataMap(m => ({ ...m, [dataKey]: data }));
      } catch (e) {
        setError({ message: e.message, reportError: e });
        setDataMap(m => ({ ...m, [dataKey]: 'error' }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    tab,
    activePresetKey,
    dataKey,
    dataMap,
    isLoading,
    kpisMap,
    channelHasKpis,
    selectedKpi,
    start,
    end,
    filterStateJson,
    tableFilterJson,
    setError
  ]);

  if (isLoadingMount) {
    return <FullPageSpinner />;
  }

  // Hoisted so DPCalendar's per-cell isOutsideRange(...) doesn't re-run
  // date-fns subDays + format for every day in the calendar view.
  const fourDaysAgo = getFormattedDaysAgo(4);

  return (
    <Page
      title='Global Benchmarking'
      pageType='Global Benchmarking'
      navs={channelNavs}
      selectedNav={tab}
      onNav={nav => {
        goToTab(nav as App2Channels);
        resetFilterState();
      }}
      actions={
        <div className='global-benchmarking-page-actions'>
          <DatePicker
            range={dateRange}
            onChange={setDateRange}
            datePresets
            isOutsideRange={date => date > fourDaysAgo}
            design={Design.BRAND}
          />
        </div>
      }
      subHeader={
        activeFilterCategories && activeFilterCategories.length > 0 ? (
          <FetchFilterBar
            categories={activeFilterCategories}
            filters={undefined}
            filterSource='currentState'
            refreshData={() => {}}
            setFilterID={() => {}}
            setFilters={() => {}}
            setFilterSource={() => {}}
            setState={setFilterState}
            state={filterState}
            simpleFilterStateOnly
          />
        ) : (
          <div className='global-benchmarking-filter-bar-placeholder' />
        )
      }
    >
      <div className='global-benchmarking'>
        <ChannelGlobalBenchmarking
          channel={tab}
          data={activeData}
          isLoading={isLoading}
          kpis={channelKpis}
          selectedKpi={selectedKpi}
          setSelectedKpi={kpi => setSelectedKpiByChannel(prev => ({ ...prev, [tab]: kpi }))}
          activePresetKey={activePresetKey}
          setActivePresetKey={key => setActivePresetByChannel(prev => ({ ...prev, [tab]: key }))}
          dateRange={dateRange}
          hasTableFilter={Boolean(tableFilterJson)}
          setTableFilterTerms={terms => setTableFilter({ baseKey, terms })}
          filterResetKey={baseKey}
        />
      </div>
    </Page>
  );
};
